// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Field.tsx
 * @input Uses React, HTMLAttributes, ReactNode, FieldLabel, IconType
 * @output Exports Field component, FieldProps
 * @position Core implementation; consumed by index.ts, tested by Field.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Field/Field.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Field/Field.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Field/index.ts (exports if types change)
 * - /apps/storybook/stories/Field.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/Field/ (showcase blocks)
 */
import { use } from 'react';
import * as stylex from '@stylexjs/stylex';
import { FieldLabel } from "./FieldLabel.js";
import { FieldStatus } from "../FieldStatus/FieldStatus.js";
import "../theme/tokens.stylex.js";
import { spacingVars, borderVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { FormLayoutContext } from "../FormLayout/FormLayoutContext.js";
import { Text } from "../Text/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  container: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    $$css: true
  },
  containerGap: {
    kOIVth: "xzye2dw",
    $$css: true
  },
  horizontalLabels: {
    k1xSpc: "xjp7ctv",
    $$css: true
  }
};

// Dynamic style for the consumer-controlled field width. Numbers are treated
// as pixels by StyleX; strings (e.g. '100%') are used as-is.
const dynamicStyles = {
  width: width => [{
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }]
};
/**
 * A form field wrapper that provides label and description.
 *
 * @example
 * ```
 * const id = useId();
 * const descID = useId();
 * <Field label="Email" description="We'll never share your email" inputID={id} descriptionID={descID}>
 *   <input id={id} aria-describedby={descID} />
 * </Field>
 * ```
 */
export function Field({
  label,
  isLabelHidden = false,
  description,
  inputID,
  labelID,
  isGroupLabel = false,
  descriptionID,
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  labelIcon,
  status,
  labelTooltip,
  statusVariant = 'attached',
  width,
  xstyle,
  children,
  className,
  style,
  ref,
  ...props
}) {
  const {
    direction
  } = use(FormLayoutContext);
  const isHorizontalLabels = direction === 'horizontal-labels';
  const resolvedDescriptionID = descriptionID ?? (description ? `${inputID}-desc` : undefined);
  const resolvedMessageID = status?.messageID ?? (status?.message ? `${inputID}-status` : undefined);
  if (isOptional && isRequired) {
    console.warn('Field: isOptional and isRequired are mutually exclusive. isOptional takes precedence.');
  }
  const labelNode = /*#__PURE__*/_jsx(FieldLabel, {
    label: label,
    inputID: inputID,
    labelID: labelID,
    isGroupLabel: isGroupLabel,
    isLabelHidden: isLabelHidden,
    isDisabled: isDisabled,
    isOptional: isOptional,
    isRequired: isRequired,
    labelIcon: labelIcon,
    labelTooltip: labelTooltip,
    description: isHorizontalLabels ? undefined : description,
    descriptionID: isHorizontalLabels ? undefined : resolvedDescriptionID
  });
  const statusNode = status?.message ? /*#__PURE__*/_jsx(FieldStatus, {
    type: status.type,
    message: status.message,
    id: resolvedMessageID,
    variant: statusVariant
  }) : null;

  // ─── Horizontal-labels mode ───────────────────────────────────────────
  // Use display:contents so the parent grid's `auto 1fr` columns place
  // the label in column 1 and the input group in column 2. Description
  // and status are grouped with the input in column 2.
  // The label wrapper gets top padding to align label text with input text.
  if (isHorizontalLabels) {
    return /*#__PURE__*/_jsxs("div", {
      ref: ref,
      ...mergeProps(themeProps('field', {
        layout: 'horizontal-labels'
      }), stylex.props(styles.horizontalLabels, xstyle), className, style),
      ...props,
      children: [/*#__PURE__*/_jsx("div", {
        ...{
          className: "x1enrzb7"
        },
        children: labelNode
      }), /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x78zum5 xdt5ytf xc8icb0"
        },
        children: [description && /*#__PURE__*/_jsx(Text, {
          type: "supporting",
          display: "block",
          id: resolvedDescriptionID,
          children: description
        }), children, statusNode]
      })]
    });
  }

  // ─── Default mode (vertical / horizontal) ─────────────────────────────
  return /*#__PURE__*/_jsxs("div", {
    ref: ref,
    ...mergeProps(themeProps('field'), stylex.props(styles.container, !isLabelHidden && styles.containerGap, width != null && dynamicStyles.width(width), xstyle), className, style),
    ...props,
    children: [labelNode, statusVariant === 'attached' ? /*#__PURE__*/_jsxs("div", {
      ...{
        className: "x78zum5 xdt5ytf xc8icb0"
      },
      children: [children, statusNode]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [children, statusNode]
    })]
  });
}
Field.displayName = 'Field';